/**
 * Guided rename migration dialog — the replacement for the clause-rename 409
 * dead end (docs/plans/2026-07-18-pr9-guided-rename-migration.md, Design 10).
 * Submits to `POST /templates/{templateId}/clauses/{clauseId}/migrate`, which
 * rewrites the active org template's Word tags with string surgery and
 * activates a new version atomically with the clause/option identity change.
 *
 * Opens UNFILLED from either ClauseForm CTA (the referencing banner, or its
 * 409 conflict block) — no auto-pairing inference; the author always states
 * the new clause ID and/or option renames explicitly. Mapping rows only
 * cover REFERENCED options (`referencedOptionIds`) — an unreferenced option
 * can be renamed through the ordinary ClauseForm edit instead.
 *
 * The live tag-mapping preview mirrors the backend's own derivation
 * (ClauseMigrationService.buildRenameMappings) using ONLY the canonical key
 * builders (ADR-024) — clause_/check_/tick_ per option plus unanswered_ once
 * per clause — so a combined clause+option rename or a swap (`yes<->no`)
 * previews exactly what the docx rewrite will do. Client-side validation
 * here is advisory only; MigrateClauseInputSchema on the server is the
 * enforcement point, and a rejected request surfaces the server's structured
 * issues (400) or conflict (409) instead.
 */

import { useMemo, useState } from 'react';
import { Button, Modal } from '../common';
import { useMigrateClause } from '../../hooks';
import { getTagValidationIssues, getClauseConflictTags } from '../../services/api';
import ConflictTagList from './ConflictTagList';
import TagPreview from './TagPreview';
import {
  clauseTagKey,
  checkTagKey,
  tickTagKey,
  unansweredTagKey,
} from '../../utils/clauseTagParser';
import type { Clause, ClauseOption } from '../../types';
import type { MigrateClauseInput, MigrateClauseResult, TagIssue } from '../../types/orgTemplate';

const TAG_SAFE_ID_RE = /^[A-Za-z0-9_-]+$/;
const TAG_SAFE_ID_MESSAGE = 'IDs may contain only letters, numbers, hyphens and underscores';

export interface ClauseMigrationDialogProps {
  templateId: string;
  clause: Clause;
  /** Bare tag keys referencing this clause — informational only here (the
   *  banner that opened this dialog already showed them); kept as a prop so
   *  ClauseManager can thread the same lookup it already has. */
  referencingTags?: string[];
  /** Option ids the active template's tags reference — one mapping row is
   *  rendered per id in this list. */
  referencedOptionIds?: string[];
  onClose: () => void;
  /** Fired the instant the migration succeeds (before the user dismisses the
   *  success summary) — the parent uses this to close any open ClauseForm,
   *  which is bound to the now-dead old clause id. */
  onMigrated: (result: MigrateClauseResult) => void;
}

interface MappingPreviewEntry {
  oldKey: string;
  newKey: string;
}

/**
 * Mirrors ClauseMigrationService.buildRenameMappings exactly: derives every
 * tag-family mapping a clause rename and/or option renames produce, using
 * only the canonical key builders. An option (or the clause-wide
 * unanswered_ tag) whose derived key is unchanged is skipped, so only
 * genuinely changed keys appear in the preview.
 */
function buildMappingPreview(
  clauseId: string,
  options: ClauseOption[],
  newClauseId: string,
  optionRenameMap: Map<string, string>
): MappingPreviewEntry[] {
  const mappings: MappingPreviewEntry[] = [];

  for (const option of options) {
    const newOptionId = optionRenameMap.get(option.id) ?? option.id;
    if (clauseId === newClauseId && option.id === newOptionId) continue;
    mappings.push(
      { oldKey: clauseTagKey(clauseId, option.id), newKey: clauseTagKey(newClauseId, newOptionId) },
      { oldKey: checkTagKey(clauseId, option.id), newKey: checkTagKey(newClauseId, newOptionId) },
      { oldKey: tickTagKey(clauseId, option.id), newKey: tickTagKey(newClauseId, newOptionId) }
    );
  }

  if (clauseId !== newClauseId) {
    mappings.push({ oldKey: unansweredTagKey(clauseId), newKey: unansweredTagKey(newClauseId) });
  }

  return mappings;
}

export default function ClauseMigrationDialog({
  templateId,
  clause,
  referencedOptionIds,
  onClose,
  onMigrated,
}: ClauseMigrationDialogProps) {
  const migrateClause = useMigrateClause();

  const [newClauseId, setNewClauseId] = useState('');
  const [optionRenameInputs, setOptionRenameInputs] = useState<Record<string, string>>({});
  const [changeNotes, setChangeNotes] = useState('');

  const [validationError, setValidationError] = useState<string | null>(null);
  const [tagErrors, setTagErrors] = useState<TagIssue[] | null>(null);
  const [conflictTags, setConflictTags] = useState<string[] | null>(null);
  const [genericError, setGenericError] = useState<string | null>(null);
  const [result, setResult] = useState<MigrateClauseResult | null>(null);

  const referencedOptions = useMemo(
    () => clause.options.filter((option) => (referencedOptionIds ?? []).includes(option.id)),
    [clause.options, referencedOptionIds]
  );

  const trimmedNewClauseId = newClauseId.trim();
  const clauseIdChanged = trimmedNewClauseId !== '' && trimmedNewClauseId !== clause.id;
  const effectiveNewClauseId = clauseIdChanged ? trimmedNewClauseId : clause.id;

  const optionRenameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const option of referencedOptions) {
      const to = (optionRenameInputs[option.id] ?? '').trim();
      if (to) map.set(option.id, to);
    }
    return map;
  }, [referencedOptions, optionRenameInputs]);

  const mappingPreview = useMemo(
    () => buildMappingPreview(clause.id, clause.options, effectiveNewClauseId, optionRenameMap),
    [clause.id, clause.options, effectiveNewClauseId, optionRenameMap]
  );

  const setOptionRenameInput = (optionId: string, value: string) => {
    setOptionRenameInputs((prev) => ({ ...prev, [optionId]: value }));
  };

  /** Advisory client-side validation mirroring MigrateClauseInputSchema —
   *  the server is the actual enforcement point. */
  const validate = (): string | null => {
    if (trimmedNewClauseId && !TAG_SAFE_ID_RE.test(trimmedNewClauseId)) {
      return `New clause ID: ${TAG_SAFE_ID_MESSAGE}`;
    }

    const rows = [...optionRenameMap.entries()];
    for (const [from, to] of rows) {
      if (!TAG_SAFE_ID_RE.test(to)) {
        return `Option "${from}" new ID: ${TAG_SAFE_ID_MESSAGE}`;
      }
      if (from === to) {
        return `Option "${from}": the new ID must differ from the current ID`;
      }
    }

    const tos = rows.map(([, to]) => to);
    if (new Set(tos).size !== tos.length) {
      return 'Option new IDs must be distinct';
    }

    if (!clauseIdChanged && rows.length === 0) {
      return 'Provide a new clause ID, at least one option rename, or both';
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGenericError(null);
    setTagErrors(null);
    setConflictTags(null);

    const error = validate();
    setValidationError(error);
    if (error) return;

    const input: MigrateClauseInput = {};
    if (clauseIdChanged) input.newClauseId = trimmedNewClauseId;
    if (optionRenameMap.size > 0) {
      input.optionRenames = [...optionRenameMap.entries()].map(([from, to]) => ({ from, to }));
    }
    if (changeNotes.trim()) input.changeNotes = changeNotes.trim();

    try {
      const migrationResult = await migrateClause.mutateAsync({
        templateId,
        clauseId: clause.id,
        input,
      });
      setResult(migrationResult);
      onMigrated(migrationResult);
    } catch (err) {
      const issues = getTagValidationIssues(err);
      if (issues) {
        setTagErrors(issues);
        return;
      }
      const tags = getClauseConflictTags(err);
      if (tags) {
        setConflictTags(tags);
      }
      setGenericError(err instanceof Error ? err.message : 'Migration failed. Please try again.');
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Migrate & rename clause" maxWidth="sm:max-w-2xl">
      {result ? (
        <div className="space-y-4">
          <div
            role="status"
            className="rounded-md border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 px-4 py-3 text-body-sm text-emerald-800 dark:text-emerald-200"
          >
            Migrated to version {result.newVersion}. {result.replacements} tag
            {result.replacements === 1 ? '' : 's'} updated.
          </div>
          {result.tagValidation.warnings.length > 0 && (
            <div
              role="status"
              className="rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-body-sm text-amber-800 dark:text-amber-200"
            >
              <p className="font-medium mb-1">Clause markup warnings from this migration</p>
              <ul className="list-disc pl-5 space-y-1">
                {result.tagValidation.warnings.map((warning, i) => (
                  <li key={`${warning.code}-${warning.tag ?? i}`}>
                    {warning.tag && <code className="font-mono">{`{${warning.tag}}`}</code>}
                    {warning.tag ? ' — ' : ''}
                    {warning.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex justify-end pt-2">
            {/* autoFocus claims focus in the same commit that removed the
                focused Migrate button (and in which the parent unmounts the
                ClauseForm beneath). Without it, focus drops to body and the
                unmounting form's focus trap would see itself as the owner
                and "restore" focus to the background page behind this open
                aria-modal dialog. */}
            <Button onClick={onClose} autoFocus>
              Done
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="max-h-[70vh] overflow-y-auto space-y-4">
          {/* Mandatory copy — Design 8 (dangling selections) and Design 9 (rollback). */}
          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <p className="text-body-sm text-amber-700 dark:text-amber-400">
              Contracts that have answered this clause will show it as unanswered until
              re-answered; their next generation will report it.
            </p>
          </div>
          <div className="p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg">
            <p className="text-body-sm text-slate-600 dark:text-slate-400">
              Rolling back to an earlier template version afterwards requires reversing this
              rename first.
            </p>
          </div>

          {/* New clause ID */}
          <div>
            <label htmlFor="migrate-new-clause-id" className="label">
              New clause ID
            </label>
            <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
              Current ID: <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded">{clause.id}</code>
            </p>
            <input
              id="migrate-new-clause-id"
              type="text"
              value={newClauseId}
              onChange={(e) => setNewClauseId(e.target.value)}
              placeholder={clause.id}
              className="input mt-1"
            />
          </div>

          {/* Option renames — one row per referenced option */}
          {referencedOptions.length > 0 && (
            <div>
              <p className="label">Option renames</p>
              <div className="mt-1 space-y-2">
                {referencedOptions.map((option) => (
                  <div key={option.id} className="flex items-center gap-2">
                    <code className="font-mono text-body-sm text-slate-600 dark:text-slate-300 shrink-0">
                      {option.id}
                    </code>
                    <span aria-hidden="true" className="text-slate-400">
                      →
                    </span>
                    <input
                      type="text"
                      aria-label={`New ID for option "${option.id}"`}
                      value={optionRenameInputs[option.id] ?? ''}
                      onChange={(e) => setOptionRenameInput(option.id, e.target.value)}
                      placeholder="unchanged"
                      className="input"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Live tag-mapping preview */}
          {mappingPreview.length > 0 && (
            <div>
              <p className="label">Tag mapping preview</p>
              <ul className="mt-1 space-y-1">
                {mappingPreview.map(({ oldKey, newKey }) => (
                  <li key={`${oldKey}->${newKey}`} className="flex items-center gap-2 flex-wrap">
                    <code className="font-mono text-caption text-slate-500 dark:text-slate-400 break-all">
                      {oldKey}
                    </code>
                    <span aria-hidden="true" className="text-slate-400">
                      →
                    </span>
                    <code className="font-mono text-caption text-brand-600 dark:text-brand-400 break-all">
                      {newKey}
                    </code>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Change notes */}
          <div>
            <label htmlFor="migrate-change-notes" className="label">
              Change notes
            </label>
            <textarea
              id="migrate-change-notes"
              value={changeNotes}
              onChange={(e) => setChangeNotes(e.target.value)}
              rows={2}
              placeholder="Optional — recorded against the new template version"
              className="textarea mt-1"
            />
          </div>

          {/* Advisory client-side validation error */}
          {validationError && (
            <div
              role="alert"
              className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
            >
              <p className="text-body-sm text-red-600 dark:text-red-400">{validationError}</p>
            </div>
          )}

          {/* Structured 400 (tag validation) errors */}
          {tagErrors && (
            <div
              role="alert"
              className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
            >
              <p className="font-medium text-body-sm text-red-600 dark:text-red-400 mb-2">
                This migration was not applied. Fix the clause markup below, then try again.
              </p>
              <ul className="list-disc pl-5 space-y-1 text-body-sm text-red-600 dark:text-red-400">
                {tagErrors.map((issue, i) => (
                  <li key={`${issue.code}-${issue.tag ?? i}`}>
                    {issue.tag && <code className="font-mono">{`{${issue.tag}}`}</code>}
                    {issue.tag ? ' — ' : ''}
                    {issue.message}
                    {issue.suggestion && (
                      <span className="mt-0.5 flex items-center gap-1.5">
                        <span className="font-medium">Suggested:</span>
                        <TagPreview
                          tag={issue.suggestion}
                          variant="plain"
                          label={`suggested tag {${issue.suggestion}}`}
                        />
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Generic / 409 conflict error */}
          {genericError && !tagErrors && (
            <div
              role="alert"
              className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
            >
              <p className="text-body-sm text-red-600 dark:text-red-400">{genericError}</p>
              {conflictTags && (
                <div className="text-body-sm text-red-600 dark:text-red-400">
                  <ConflictTagList tags={conflictTags} />
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200 dark:border-slate-700">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={migrateClause.isPending}>
              Migrate
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
